import React from "react";
import { useForm } from "react-hook-form";
import { Button, Input } from "../../atoms";
import { FormField } from "../FormField";
import { FormHelpCallout } from "../FormHelpCallout";
import { calculateAssetValue } from "@burnchart/financial";
import { safeDate } from "../../../utils/date";
import type { ReviseAssetEventData } from "../../../services/api/financial.service";
import type { TimelineEvent } from "../../../types/startup.types";

export interface AssetRevisionFormProps {
  asset: TimelineEvent;
  defaultEffectiveDate?: string;
  onSubmit: (data: ReviseAssetEventData) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

interface AssetRevisionFormValues {
  effectiveDate: string;
  amount: number;
  monthlyAppreciationRate: number;
  minimumValue?: number;
  revisionNote?: string;
}

export const AssetRevisionForm: React.FC<AssetRevisionFormProps> = ({
  asset,
  defaultEffectiveDate,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const initialEffectiveDate =
    defaultEffectiveDate || new Date().toISOString().split("T")[0];

  const modeledBalance = React.useMemo(() => {
    const asOf = safeDate(initialEffectiveDate);
    return calculateAssetValue(
      {
        type: "asset",
        date: safeDate(asset.date),
        amount: asset.amount,
        monthlyAppreciationRate: asset.monthlyAppreciationRate,
        minimumValue: asset.minimumValue,
      },
      asOf,
    );
  }, [asset, initialEffectiveDate]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<AssetRevisionFormValues>({
    defaultValues: {
      effectiveDate: initialEffectiveDate,
      amount: Math.round(modeledBalance * 100) / 100,
      monthlyAppreciationRate: asset.monthlyAppreciationRate
        ? asset.monthlyAppreciationRate * 100
        : 0,
      minimumValue: asset.minimumValue,
      revisionNote: "",
    },
  });

  const effectiveDate = watch("effectiveDate");
  const modeledAsOfEffectiveDate = React.useMemo(() => {
    const asOf = safeDate(effectiveDate);
    if (!asOf) return modeledBalance;
    return calculateAssetValue(
      {
        type: "asset",
        date: safeDate(asset.date),
        amount: asset.amount,
        monthlyAppreciationRate: asset.monthlyAppreciationRate,
        minimumValue: asset.minimumValue,
      },
      asOf,
    );
  }, [asset, effectiveDate, modeledBalance]);

  const handleFormSubmit = (data: AssetRevisionFormValues) => {
    onSubmit({
      effectiveDate: data.effectiveDate,
      amount: Number(data.amount),
      monthlyAppreciationRate: Number(data.monthlyAppreciationRate) / 100,
      minimumValue:
        data.minimumValue != null && data.minimumValue !== ("" as unknown as number)
          ? Number(data.minimumValue)
          : undefined,
      revisionNote: data.revisionNote?.trim() || undefined,
    });
  };

  const assetLabel = asset.description || asset.title || "Asset";

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <FormHelpCallout>
        Revise <strong>{assetLabel}</strong> from a date forward with a new
        balance and monthly rate. History before that date stays as originally
        modeled.
      </FormHelpCallout>

      <FormField label="Effective date" error={errors.effectiveDate?.message}>
        <Input
          type="date"
          {...register("effectiveDate", {
            required: "Effective date is required",
          })}
        />
      </FormField>

      <div className="rounded-[14px] border border-white/10 bg-white/5 px-4 py-3 text-sm text-white/80">
        Modeled balance as of {effectiveDate || initialEffectiveDate}:{" "}
        <span className="font-medium text-white">
          ${modeledAsOfEffectiveDate.toLocaleString(undefined, { maximumFractionDigits: 0 })}
        </span>
      </div>

      <FormField
        label="New balance"
        error={errors.amount?.message}
        helperText="Actual balance after rebalance, contributions, or market moves."
      >
        <Input
          type="number"
          step="0.01"
          {...register("amount", {
            required: "Balance is required",
            min: { value: 0, message: "Balance must be positive" },
            valueAsNumber: true,
          })}
        />
      </FormField>

      <FormField
        label="New monthly appreciation rate (%)"
        error={errors.monthlyAppreciationRate?.message}
        helperText="Per month, not annual. Use negative values for depreciation."
      >
        <Input
          type="number"
          step="0.01"
          {...register("monthlyAppreciationRate", {
            required: "Rate is required",
            valueAsNumber: true,
          })}
        />
      </FormField>

      <FormField
        label="Minimum value (optional)"
        error={errors.minimumValue?.message}
        helperText="Salvage value — asset will not depreciate below this amount."
      >
        <Input
          type="number"
          step="0.01"
          {...register("minimumValue", {
            min: { value: 0, message: "Minimum value must be positive" },
            valueAsNumber: true,
          })}
          placeholder="0.00"
        />
      </FormField>

      <FormField label="Note (optional)" error={errors.revisionNote?.message}>
        <Input
          {...register("revisionNote")}
          placeholder="e.g. Q2 rebalance, rolled into bonds"
        />
      </FormField>

      <div className="flex gap-3 pt-2">
        <Button
          type="submit"
          variant="primary"
          size="md"
          disabled={isLoading}
          className="flex-1"
        >
          {isLoading ? "Saving revision..." : "Save revision"}
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="md"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default AssetRevisionForm;
